import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ReactComponent as CloseIcon } from '@blink/assets/src/icons/close.svg';

import { cn } from '@/shared/lib/tailwind-merge';

const alertVariants = cva(
    'relative flex w-fit max-w-[600px] items-start gap-3 rounded-[4px] border px-[16px] py-[12px]',
    {
        variants: {
            variant: {
                default: 'bg-background text-foreground border-gray-300',
                destructive: 'bg-light-negative text-content-negative border-none',
                positive: 'bg-light-positive text-content-positive border-none',
                warning: 'bg-light-warning text-content-warning border-none',
                info: 'bg-light-accent text-content-accent border-none',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

interface AlertProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof alertVariants> {
    icon?: React.ReactNode;
    closable?: boolean;
    onClose?: () => void;
    horizontal?: boolean;
}

const BaseAlert = React.forwardRef<HTMLDivElement, AlertProps>(
    ({ className, variant, icon, closable, onClose, horizontal, children, ...props }, ref) => {
        const [isVisible, setIsVisible] = React.useState(true);

        if (!isVisible) return null;

        const handleClose = () => {
            setIsVisible(false);
            if (onClose) onClose();
        };

        return (
            <div
                ref={ref}
                role='alert'
                className={cn(alertVariants({ variant }), 'flex-row items-start', className)}
                {...props}
            >
                {icon && (
                    <span className='flex-shrink-0 flex items-center justify-center gap-2 mt-1'>
                        {icon}
                    </span>
                )}
                <div
                    className={cn(
                        'flex',
                        horizontal
                            ? 'flex-row items-center gap-4 flex-1'
                            : 'flex-col items-start gap-[4px] flex-1',
                    )}
                >
                    {children}
                </div>
                {closable && (
                    <button onClick={handleClose} className='text-inherit' aria-label='Close'>
                        <CloseIcon className='h-4 w-4' />
                    </button>
                )}
            </div>
        );
    },
);
BaseAlert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
    ({ className, ...props }, ref) => (
        <h5
            ref={ref}
            className={cn('font-medium leading-none tracking-tight', className)}
            {...props}
        />
    ),
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            'text-[14px] font-[500] leading-[20px] letter-spacing-[-0.42px] font-inter font-feature-settings-["calt"_off]',
            className,
        )}
        {...props}
    />
));
AlertDescription.displayName = 'AlertDescription';

export { BaseAlert, AlertTitle, AlertDescription };
